import { defineStore } from 'pinia'
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { updateGlobalOptions } from 'vue3-toastify';
import { toastOptions } from '@/config/toast';
import type { RouteLocationNormalized } from 'vue-router';
import { DISTRIBUTION } from '@/config/distribution';

/** The phone layout of `src/theme/auth-shell.css`: below its 900px breakpoint. */
export const PHONE_LAYOUT = '(max-width: 899px)';

/**
 * Whether the page is drawn under the status bar, measured with the inset the CSS reads.
 * Android only does that from Android 15 with a WebView of 140 or newer; otherwise the
 * page starts below the bar, the bar shows the window background, and light icons on
 * it would vanish. iOS is not measured: there `followRoute` moves the page under the bar.
 */
function pageUnderStatusBar(): boolean {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position: fixed; visibility: hidden; height: var(--ion-safe-area-top, env(safe-area-inset-top, 0px))';
  document.body.appendChild(probe);
  const under = probe.offsetHeight > 0;
  probe.remove();
  return under;
}

interface ThemeState {
  isDarkMode: boolean;
  /** A brand photo lies under the status bar (see `followRoute`). */
  heroUnderStatusBar: boolean;
}

export const useThemeStore = defineStore('theme', {
  state: (): ThemeState => ({
    isDarkMode: false,
    heroUnderStatusBar: false,
  }),

  actions: {
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      this.applyTheme();
      this.savePreference();
    },

    setDarkMode(enabled: boolean) {
      this.isDarkMode = enabled;
      this.applyTheme();
      this.savePreference();
    },

    applyTheme() {
      if (this.isDarkMode) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
      this.updateStatusBar();
      this.updateToastTheme();
    },

    updateToastTheme() {
      // Replaces the options rather than merging, so the app's own go along.
      updateGlobalOptions({ ...toastOptions, theme: this.isDarkMode ? 'dark' : 'light' });
    },

    /**
     * On a phone, the signed-out screens (routes with `meta.authShell`) of a brand
     * with a photo run that photo up behind the status bar (`src/theme/auth-shell.css`).
     * Dark icons are unreadable there, so the bar gets light ones, and on iOS the web
     * view has to reach under the bar first. Every other screen keeps the theme.
     */
    followRoute(route: RouteLocationNormalized) {
      this.heroUnderStatusBar =
        Boolean(route.meta.authShell) &&
        Boolean(DISTRIBUTION.heroImage) &&
        window.matchMedia(PHONE_LAYOUT).matches &&
        (Capacitor.getPlatform() !== 'android' || pageUnderStatusBar());
      this.updateStatusBar();
    },

    updateStatusBar() {
      if (!Capacitor.isNativePlatform()) return;

      const hero = this.heroUnderStatusBar;

      if (Capacitor.getPlatform() === 'android') {
        const themed = this.isDarkMode ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
        SystemBars.setStyle({ style: hero ? SystemBarsStyle.Dark : themed, bar: SystemBarType.StatusBar }).catch(() => {});
        SystemBars.setStyle({ style: themed, bar: SystemBarType.NavigationBar }).catch(() => {});
      } else {
        StatusBar.setBackgroundColor({ color: this.isDarkMode ? '#1a1a1a' : '#FFFFFF' }).catch(() => {});
        // `main.ts` starts iOS with the web view below the bar; only the photo goes under it.
        StatusBar.setOverlaysWebView({ overlay: hero }).catch(() => {});
        StatusBar.setStyle({ style: hero ? Style.Dark : Style.Default }).catch(() => {});
      }
    },

    // Android resets the system-bar appearance flags (icon color) after events
    // like keyboard show/hide, app resume, fullscreen video, or some route
    // transitions. Re-applying the current style keeps icons readable.
    reapplySystemBars() {
      this.updateStatusBar();
    },

    savePreference() {
      localStorage.setItem('theme-preference', this.isDarkMode ? 'dark' : 'light');
    },

    loadPreference() {
      const saved = localStorage.getItem('theme-preference');
      if (saved) {
        this.isDarkMode = saved === 'dark';
      } else {
        // Check system preference
        this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      this.applyTheme();
    }
  }
});
